import type { WritableComputedRef } from "vue";

import { useI18n } from "vue-i18n";

export interface TranslationParams {
  key: string;
  params?: Record<string, string | number>;
}

export interface TranslationKey {
  key: string;
}

export type TranslationKeyFn = (args: any) => TranslationParams;

export function keyOf(k: TranslationParams | TranslationKeyFn): string {
  if (typeof k === "function") {
    return k({}).key;
  }

  return k.key;
}

export type TypedIntl<TKeys> = {
  [key: string]: unknown;
  d: ReturnType<typeof useI18n>["d"];
  n: ReturnType<typeof useI18n>["n"];
  locale: WritableComputedRef<string, string>;
  rawT: (key: string, params?: Record<string, string | number>) => string;
  keyOf: typeof keyOf;
  k: TKeys;
  t: (translationParams: TranslationParams) => string;
  tm: ReturnType<typeof useI18n>["tm"];
  rt: ReturnType<typeof useI18n>["rt"];
  te: ReturnType<typeof useI18n>["te"];
};

export function createTypedIntl<const TKeys>(keys: TKeys): () => TypedIntl<TKeys> {
  return function useTypedIntl(): TypedIntl<TKeys> {
    const { t: rawT, ...intl } = useI18n();

    function typedT({ key, params }: TranslationParams): string {
      return rawT(key, params || {});
    }

    return {
      ...intl,
      rawT: (key: string, params?: Record<string, string | number>) => rawT(key, params || {}),
      keyOf,
      k: keys,
      t: typedT,
    } as unknown as TypedIntl<TKeys>;
  };
}
